import * as Sentry from '@sentry/browser'
import { useMutation } from '@tanstack/react-query'
import type { InlineResponse2004 } from '@ulysses-inc/harami_api_client'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { getTemplates } from 'src/state/ducks/templateList/actions'
import { extractHttpErrorMessages } from 'src/state/middleware/saga/handleHttpError'
import { RootState } from 'src/state/store'
import { MigrationDomainError } from '../domain/types'
import { runGridToTableMigration } from '../workflow'

const errorMessage = (error: unknown): string => {
  const httpErrorMessage = extractHttpErrorMessages(error)
  if (httpErrorMessage) return httpErrorMessage
  if (error instanceof Error && error.message) return error.message
  return 'ひな形の移行に失敗しました'
}

export const useGridToTableMigration = () => {
  const dispatch = useDispatch()
  const { request, filter } = useSelector(
    (state: RootState) => state.templateListState,
  )
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar()

  return useMutation<InlineResponse2004, unknown, number>({
    mutationFn: templateId => runGridToTableMigration(templateId),
    onSuccess: () => {
      showSuccessSnackbar('移行後の新しいひな形が作成されました')
      dispatch(getTemplates(request, filter))
    },
    onError: (error, templateId) => {
      if (error instanceof MigrationDomainError) return
      // 既存 API の組み合わせで動き、API 単体のエラーからは移行中の失敗と区別できないため、
      // 移行機能のエラーであることを明示する
      Sentry.captureException(
        new Error('ひな形の移行に失敗しました', { cause: error }),
        {
          tags: { feature: 'gridToTableMigration' },
          extra: { templateId, error },
        },
      )
      showErrorSnackbar(errorMessage(error))
    },
  })
}
